import axios from 'axios';
import { API_BASE } from './apiBase';

// Separate axios instance for the B2B portal so portal sessions never
// collide with admin sessions (different JWT scope on the backend).
//
// Wave-2 auth migration (Feb 2026): the portal now authenticates via
// an httpOnly cookie (`uc_portal_token`) that the backend sets on
// verify-otp / set-session and clears on logout. No token material
// lives in localStorage — that surface was removed to close the XSS
// theft vector flagged in the security audit.
const portalApi = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

portalApi.interceptors.response.use(
    (r) => r,
    (error) => {
        // Token expired or invalid → bounce to portal login. The public
        // OTP endpoints (/portal/auth/…) legitimately return 401 for
        // bad codes; ignore those. Also skip when the user is currently
        // on the admin app or the portal login page — otherwise the
        // admin app's mount-time /portal/me probe would kick admins
        // out of their own dashboard.
        if (error.response?.status === 401) {
            const url = error.config?.url || '';
            const path = window.location.pathname;
            // Strict segment match — `/portal-customers` is an ADMIN
            // route (not portal) and must not trigger the redirect.
            const isPortalPath = path === '/portal' || path.startsWith('/portal/');
            const onPortalProtectedRoute = isPortalPath && path !== '/portal/login';
            if (!url.includes('/portal/auth/') && onPortalProtectedRoute) {
                window.location.href = '/portal/login';
            }
        }
        return Promise.reject(error);
    },
);

export const portalAuthAPI = {
    requestOtp: (email, locale = 'fr') =>
        portalApi.post('/portal/auth/request-otp', { email, locale }),
    verifyOtp: (email, code) =>
        portalApi.post('/portal/auth/verify-otp', { email, code }),
    setSession: (token, email) =>
        portalApi.post('/portal/auth/set-session', { token, email }),
    logout: () => portalApi.post('/portal/auth/logout'),
};

export const portalMeAPI = {
    get: () => portalApi.get('/portal/me'),
    update: (payload) => portalApi.put('/portal/me', payload),
};

export const portalCatalogAPI = {
    brands: () => portalApi.get('/portal/catalog/brands'),
    categories: () => portalApi.get('/portal/catalog/categories'),
    products: (params) => portalApi.get('/portal/catalog/products', { params }),
    featured: (params) => portalApi.get('/portal/catalog/featured', { params }),
    detail: (sku) => portalApi.get(`/portal/catalog/products/${encodeURIComponent(sku)}`),
};

export const portalCartAPI = {
    get: () => portalApi.get('/portal/cart'),
    addItem: (product_id, qty) => portalApi.post('/portal/cart/items', { product_id, qty }),
    updateItem: (product_id, qty) => portalApi.patch(`/portal/cart/items/${product_id}`, { qty }),
    removeItem: (product_id) => portalApi.delete(`/portal/cart/items/${product_id}`),
    clear: () => portalApi.delete('/portal/cart'),
    submit: (notes, order_type = 'order') =>
        portalApi.post('/portal/cart/submit', { notes, order_type }),
};

export const portalOrdersAPI = {
    list: (limit = 50) => portalApi.get('/portal/orders', { params: { limit } }),
    reorder: (order_id) => portalApi.post(`/portal/orders/${order_id}/reorder`),
};

export default portalApi;
